import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
    FEATURED_PROJECTS,
    PROJECT_CATEGORIES,
} from "../data/content";
import { useReveal } from "../hooks/useReveal";

// Extend the base projects to a rich masonry gallery. Each tile is tagged with a category
// so it also appears when a category filter is selected. We reuse existing generated images
// where an aspect fits multiple categories (e.g., a villa's living room appears under both
// Villa and Living), which keeps every filter populated with at least 3–5 tiles.
const EXTRA_TILES = [
    // ---------- Kitchen ----------
    { id: "e-k1", category: "Kitchen", title: "Marble & Oak Island", cover: "/generated/modular-kitchen.jpg", h: 520 },
    { id: "e-k2", category: "Kitchen", title: "The Baker's Kitchen", cover: "/generated/dining-room.jpg", h: 460 },
    // ---------- Bedroom ----------
    { id: "e-b1", category: "Bedroom", title: "Cocoon Wardrobes", cover: "/generated/cnc-wardrobes.jpg", h: 520 },
    { id: "e-b2", category: "Bedroom", title: "The Walk-in Closet", cover: "/generated/walk-in-closet.jpg", h: 640 },
    { id: "e-b3", category: "Bedroom", title: "The Kids' Room", cover: "/generated/kids-room.jpg", h: 480 },
    // ---------- Living ----------
    { id: "e-l1", category: "Living", title: "The Dining Room", cover: "/generated/dining-room.jpg", h: 620 },
    { id: "e-l2", category: "Living", title: "Fluted TV Wall", cover: "/generated/luxury-tv-unit.jpg", h: 540 },
    { id: "e-l3", category: "Living", title: "Sunlit Living", cover: "/generated/quiet-apartment-living.jpg", h: 500 },
    // ---------- Villa ----------
    { id: "e-v1", category: "Villa", title: "The Ensuite", cover: "/generated/luxury-washroom.jpg", h: 700 },
    { id: "e-v2", category: "Villa", title: "Villa at Dusk", cover: "/generated/villa-exterior.jpg", h: 560 },
    { id: "e-v3", category: "Villa", title: "Villa Master Suite", cover: "/generated/master-bedroom-suite.jpg", h: 460 },
    // ---------- Apartment ----------
    { id: "e-a1", category: "Apartment", title: "The Foyer", cover: "/generated/foyer.jpg", h: 480 },
    { id: "e-a2", category: "Apartment", title: "The Pooja Room", cover: "/generated/pooja-room.jpg", h: 620 },
    { id: "e-a3", category: "Apartment", title: "Modular Kitchen", cover: "/generated/modular-kitchen.jpg", h: 500 },
    // ---------- Office ----------
    { id: "e-o1", category: "Office", title: "The Home Office", cover: "/generated/home-office.jpg", h: 540 },
    { id: "e-o2", category: "Office", title: "The Studio", cover: "/generated/studio-in-black.jpg", h: 460 },
    // ---------- Landscape ----------
    { id: "e-ls1", category: "Landscape", title: "Balcony Landscape", cover: "/generated/balcony-landscape.jpg", h: 620 },
    { id: "e-ls2", category: "Landscape", title: "Villa Landscape", cover: "/generated/villa-exterior.jpg", h: 500 },
    { id: "e-ls3", category: "Landscape", title: "The Terrace Garden", cover: "/generated/balcony-landscape.jpg", h: 440 },
];

const Portfolio = () => {
    const [active, setActive] = useState("All");
    const ref = useReveal();

    const combined = useMemo(() => {
        const base = FEATURED_PROJECTS.map((p) => ({
            id: p.id,
            title: p.title,
            category: p.category,
            cover: p.cover,
            link: `/portfolio/${p.id}`,
            location: p.location,
            h: 600,
        }));
        const extras = EXTRA_TILES.map((e) => ({
            id: e.id,
            title: e.title,
            category: e.category,
            cover: e.cover,
            link: null,
            location: "Bengaluru",
            h: e.h,
        }));
        return [...base, ...extras];
    }, []);

    const filtered = useMemo(
        () =>
            active === "All"
                ? combined
                : combined.filter((c) => c.category === active),
        [active, combined]
    );

    return (
        <div ref={ref} data-testid="portfolio-page" className="pt-24 md:pt-32">
            {/* Header */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="reveal">
                    <p className="eyebrow mb-6">The Portfolio</p>
                    <h1
                        className="font-display text-samora-ink leading-[0.95] tracking-tight"
                        style={{ fontSize: "clamp(2.75rem, 8vw, 8rem)" }}
                    >
                        Homes we've<br />
                        <span className="italic font-serif text-samora-gold">
                            quietly built.
                        </span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-samora-text/75 text-base md:text-lg leading-relaxed">
                        A selection of villas, apartments, kitchens and studios
                        across Bengaluru and beyond. Each project is a
                        conversation between our clients, our craftsmen, and
                        the light of a specific room.
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section
                data-testid="portfolio-filters"
                className="sticky top-20 md:top-24 z-30 bg-samora-bone/95 backdrop-blur-md border-y border-samora-line"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 overflow-x-auto no-scrollbar">
                    <div className="flex items-center gap-8 md:gap-12 py-5 whitespace-nowrap">
                        {PROJECT_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                data-testid={`filter-${c.toLowerCase()}`}
                                onClick={() => setActive(c)}
                                className={`text-xs md:text-sm tracking-[0.28em] uppercase transition-colors ${
                                    active === c
                                        ? "text-samora-gold"
                                        : "text-samora-ink/60 hover:text-samora-ink"
                                }`}
                            >
                                {c}
                                {active === c && (
                                    <span className="ml-2 text-samora-gold">
                                        ·
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Masonry */}
            <section className="py-16 md:py-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="masonry">
                        {filtered.map((p, idx) => {
                            const Tile = (
                                <div
                                    className="img-hover-wrap"
                                    style={{ height: `${p.h}px` }}
                                >
                                    <img
                                        src={p.cover}
                                        alt="Samora Spaces portfolio interior"
                                        loading="lazy"
                                    />
                                    {p.link && (
                                        <div className="img-hover-overlay">
                                            <div className="text-white w-full flex items-end justify-end">
                                                <ArrowUpRight
                                                    size={22}
                                                    strokeWidth={1.5}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                            return p.link ? (
                                <Link
                                    key={`${p.id}-${idx}`}
                                    to={p.link}
                                    data-testid={`portfolio-tile-${p.id}`}
                                    className="block"
                                >
                                    {Tile}
                                </Link>
                            ) : (
                                <div
                                    key={`${p.id}-${idx}`}
                                    data-testid={`portfolio-tile-${p.id}`}
                                    className="block"
                                >
                                    {Tile}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Portfolio;
